

import React, { Component } from 'react';

import cx from 'classnames';
import _debounce from 'lodash.debounce';
import _memoize from 'lodash.memoize';
import { EditorState, getDefaultKeyBinding } from 'draft-js';
import onEnterPressed from '@shootsta/on-enter-pressed';
import Editor from 'draft-js-plugins-editor';
import createMentionPlugin from 'draft-js-mention-plugin';
import { Text } from '../../../ui-kit';
import RichUtils from './ExtendedRichUtils';
import MentionItem from './MentionItem';
import MentionTag from './MentionTag';
import positionSuggestions from '../utils/positionSuggestions';
import { getUser } from '../../../common/utils';
import StyleControls from './StyleControls';

import '../styles/_rich-text-editor.scss';

const ALIGNMENT_DATA_KEY = 'textAlignment';

const getBlockStyle = (contentBlock) => {
  const classes = [];

  if (contentBlock.getType() === 'blockquote') {
    classes.push('rich-editor__blockquote');
  }

  const textAlignStyle = contentBlock.getData().get(ALIGNMENT_DATA_KEY);

  switch (textAlignStyle) {
    case 'right':classes.push('align-right');
      break;
    case 'center':classes.push('align-center');
      break;
    case 'left':classes.push('align-left');
      break;
    case 'justify':classes.push('align-justify');
      break;
    default:void 0;
  }

  return classes.join(' ');
};


























export default class RichTextEditor extends
Component {




  toggleBlockType = _memoize((blockType) => () => {
    const { editorState, onUpdateContent } = this.props;
    const currentBlockType = RichUtils.getCurrentBlockType(editorState);

    // Stops the block styling overlaying on top of any placeholder
    this.setState({
      hidePlaceholder: currentBlockType === 'unstyled' || blockType !== currentBlockType
    });

    onUpdateContent(RichUtils.toggleBlockType(editorState, blockType));
  });

  toggleAlignment = _memoize((alignment) => () => {
    const { editorState, onUpdateContent } = this.props;

    onUpdateContent(RichUtils.toggleAlignment(editorState, alignment));
  });

  toggleInlineStyle = _memoize((inlineStyle) => () => {
    const { editorState, onUpdateContent } = this.props;

    onUpdateContent(RichUtils.toggleInlineStyle(editorState, inlineStyle));
  });

  static defaultProps = {
    onUpdateContent: Function.prototype
  };

  constructor() {
    super();

    this.state = {
      suggestions: [],
      cachedUsers: new Map(),
      hidePlaceholder: false
    };

    this.mentionPlugin = createMentionPlugin({
      mentions: [],
      positionSuggestions,
      theme: { mentionSuggestions: 'mentions-dropdown' },
      mentionComponent: MentionTag
    });
  }

  componentDidMount() {
    const { setFocusOnMount = true } = this.props;

    if (setFocusOnMount) {
      setTimeout(this.focus, 0);
    }
  }

  // fix for sentry error .focus() on null
  // avoid race condition in focus()
  componentWillUnmount() {
    this._editorRef = null;
  }

  onSearchChange = _debounce(async ({ value }) => {
    const { getUsersByName, organisationId } = this.props;

    if (!value || !getUsersByName) {return;}

    const { cachedUsers } = this.state;

    const {
      data, error
    } = cachedUsers.get(value) || (await getUsersByName({
      organisationId, name: value
    }));

    if (!data || error) {return;}

    const { getUsersByName: users } = data;

    const usersWithName = users.map(getUser);

    cachedUsers.set(value, { getUsersByName: usersWithName });

    this.setState({ suggestions: usersWithName });
  }, 250);

  onTab = (e) => {
    const { editorState, onUpdateContent } = this.props;

    const maxDepth = 4;
    onUpdateContent(RichUtils.onTab(e, editorState, maxDepth));
  };

  handleKeyCommand = (command, editorState) => {
    const { onUpdateContent } = this.props;
    const newState = RichUtils.handleKeyCommand(editorState, command);

    if (newState) {
      onUpdateContent(newState);
      return 'handled';
    }

    return 'non-handled';
  };

  handleKeyPress = (e) => {
    const { onSave } = this.props;
    const { key, shiftKey } = e;

    if (onSave && !shiftKey && key === 'Enter') {
      return onSave(e);
    }

    return getDefaultKeyBinding(e);
  };

  handleEscape = () => {
    const { onCancel } = this.props;

    return onCancel ? void onCancel() : void 0;
  };

  focus = (e) => {
    if (e) {
      e.stopPropagation();
    }

    if (!this._editorRef) {return;}

    this._editorRef.focus();
  };

  setEditorRef = (editorRef) => {
    if (!editorRef) {return;}

    this._editorRef = editorRef;
  };

  onBlurHandler = (event) => {
    const { onBlur, disabled } = this.props;

    if (!onBlur ||
    disabled ||
    !event ||
    event.currentTarget.contains(event.relatedTarget)) {
      return;
    }

    onBlur(event);
  };

  render() {
    const {
      onUpdateContent,
      editorState,
      placeholder,
      hideControls,
      disabled,
      children,
      onFocus,
      label
    } = this.props;
    const { suggestions, hidePlaceholder } = this.state;
    const { MentionSuggestions } = this.mentionPlugin;
    const plugins = [this.mentionPlugin];

    return (
      <div className="rich-editor" onBlur={this.onBlurHandler}>
        {label && <Text label>{label}</Text>}
        {
        !hideControls && !disabled ?
        <StyleControls
          editorState={editorState}
          onToggle={this.toggleBlockType}
          onToggleInline={this.toggleInlineStyle}
          onToggleAlignment={this.toggleAlignment} /> :

        null}

        <div
          className={cx(
            'rich-editor__editor',
            {
              'rich-editor__editor--no-controls': hideControls || disabled,
              'rich-editor__editor--disabled': disabled
            }
          )}
          onClick={this.focus}
          role="textbox"
          tabIndex={0}
          onKeyPress={onEnterPressed({ allowPropagation: true })}>

          <Editor
            blockStyleFn={getBlockStyle}
            editorState={editorState}
            onChange={onUpdateContent}
            plugins={plugins}
            ref={this.setEditorRef}
            placeholder={hidePlaceholder ? void 0 : placeholder}
            keyBindingFn={this.handleKeyPress}
            onTab={this.onTab}
            onEscape={this.handleEscape}
            handleKeyCommand={this.handleKeyCommand}
            readOnly={disabled}
            onFocus={onFocus} />

          <MentionSuggestions
            onSearchChange={this.onSearchChange}
            suggestions={suggestions}
            entryComponent={MentionItem} />

          {children}
        </div>
      </div>);

  }
}